import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import * as moduleState from './state'
import { IStateData } from './state'
import store from 'src/store'
import * as moduleSelectors from './selectors'
import { actions as moduleActions } from './actions'
import moduleSagas from './sagas'
import { builderSagaCommunications } from 'src/helpers/redux'
import { IConnectedAccount } from '../account/state'
import { IResponse } from 'src/services/accounts/http'

// the own list and the workspace list carry the same numbers: a change lands in both
const upsertNumber = (
  list: IConnectedAccount[],
  account: IConnectedAccount
): IConnectedAccount[] =>
  list.some((number) => number.id === account.id)
    ? list.map((number) =>
        number.id === account.id ? { ...number, ...account } : number
      )
    : [...list, { ...account, number: { phoneNumber: account.identifier } }]

export const emailSlice = createSlice({
  name: moduleState.PREFIX,
  initialState: moduleState.initialState,
  reducers: {
    setAvailableNumbers(
      state,
      action: PayloadAction<moduleState.IState['data']['availableNumbers']>
    ) {
      state.data.availableNumbers = action.payload
    },
    setAvailableRcNumbers(
      state,
      action: PayloadAction<moduleState.IState['data']['availableNumbers']>
    ) {
      state.data.availableRcNumbers = action.payload
    },
    removeAssignedNumber(
      state,
      action: PayloadAction<{
        communicationNumber?: number
        phoneNumber?: string
      }>
    ) {
      if (action.payload.communicationNumber) {
        state.data.availableNumbers = state.data.availableNumbers?.filter(
          (number) => number.id !== action.payload.communicationNumber
        )
      }
      if (action.payload.phoneNumber) {
        state.data.availableRcNumbers = state.data.availableRcNumbers?.filter(
          (number) => number.phoneNumber !== action.payload.phoneNumber
        )
      }
    },
    releaseNumber(state, action: PayloadAction<number[]>) {
      // state.data.accounts.data = state.data.accounts.data.map((acc) => {
      //   if (action.payload.find((id) => acc.id === id)) {
      //     return { ...acc, status: 'inactive' }
      //   } else {
      //     return acc
      //   }
      // })

      state.data.accounts.data = state.data.accounts.data.filter((acc) => {
        return !action.payload.find((id) => acc.id === id)
      })
      state.data.workspaceAccounts.data =
        state.data.workspaceAccounts.data.filter(
          (acc) => !action.payload.find((id) => acc.id === id)
        )
    },
    setUpdateNumberState(state, action: PayloadAction<IConnectedAccount>) {
      state.data.accounts.data = upsertNumber(
        state.data.accounts.data,
        action.payload
      )
      state.data.workspaceAccounts.data = upsertNumber(
        state.data.workspaceAccounts.data,
        action.payload
      )
    },
    setWorkspaceAccounts(
      state,
      action: PayloadAction<
        IResponse<IConnectedAccount[]> & { listSearch?: string }
      >
    ) {
      state.data.workspaceAccounts = {
        data: action.payload.data.filter((i) => i.status != 'inactive'),
        cursor: action.payload.meta.nextCursor,
        listSearch: action.payload.listSearch ?? '',
      }
    },
    setFetchedMoreWorkspaceData(
      state,
      action: PayloadAction<IResponse<IConnectedAccount[]>>
    ) {
      state.data.workspaceAccounts = {
        ...state.data.workspaceAccounts,
        data: [...state.data.workspaceAccounts.data, ...action.payload.data],
        cursor: action.payload.meta.nextCursor,
      }
    },
    setFetchedMoreData(
      state,
      action: PayloadAction<IResponse<IConnectedAccount[]>>
    ) {
      state.data.accounts = {
        ...state.data.accounts,
        data: [...state.data.accounts.data, ...action.payload.data],
        cursor: action.payload.meta.nextCursor,
      }
    },

    setConnectedAccounts(
      state,
      action: PayloadAction<
        IResponse<IConnectedAccount[]> & { listSearch?: string }
      >
    ) {
      state.data.accounts = {
        ...state.data.accounts,
        data: action.payload.data.filter((i) => i.status != 'inactive'),
        cursor: action.payload.meta.nextCursor,
        listSearch: action.payload.listSearch ?? '',
      }
    },

    setAccountItem(
      state,
      action: PayloadAction<IStateData['accounts']['item']>
    ) {
      const { payload } = action
      state.data.accounts.item = payload
    },

    setAccountsUpdate(
      state,
      action: PayloadAction<IStateData['accounts']['item']>
    ) {
      const { payload } = action
      if (payload) {
        state.data.accounts.item = {
          ...state.data.accounts.item,
          ...payload,
        }
        const patch = (item: IConnectedAccount) =>
          item.id === payload.id
            ? { ...item, ...(payload as unknown as IConnectedAccount) }
            : item

        state.data.accounts.data = state.data.accounts.data.map(patch)
        state.data.workspaceAccounts.data =
          state.data.workspaceAccounts.data.map(patch)
      }
    },
    setAccountsDelete(state, action: PayloadAction<{ accountId: number }>) {
      const { payload } = action
      state.data.accounts.data = state.data.accounts.data.filter(
        (item) => item.id !== payload.accountId
      )
      state.data.workspaceAccounts.data =
        state.data.workspaceAccounts.data.filter(
          (item) => item.id !== payload.accountId
        )
    },
  },
  extraReducers: (builder) => {
    builderSagaCommunications<moduleState.IState>(builder, moduleActions)
  },
})

export const reducers = { [moduleState.PREFIX]: emailSlice.reducer }
export const selectors = moduleSelectors
export const setters = emailSlice.actions
export const actions = moduleActions
export const sagas = moduleSagas

export const injectStore = () => {
  store.injectReducer?.(moduleState.PREFIX, emailSlice.reducer)
  store.injectSaga?.(moduleState.PREFIX, sagas)
}
